import { useEffect, useRef } from 'react';
import { toneForApp } from '../lib/connectedApps';
import type { ConnectedApp } from '../lib/connectedApps';

/**
 * 外部サイト（連携アプリ）へ移動する前に挟む確認モーダル。
 * 実装は PublishConfirmModal.tsx の作法を踏襲（Escape で閉じる・body スクロールロック等）。
 */
interface ExternalLinkModalProps {
  open: boolean;
  app: ConnectedApp | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ExternalLinkModal({ open, app, onCancel, onConfirm }: ExternalLinkModalProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open || !app) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="external-link-modal-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="external-link-modal-title" className="modal__title">外部サイトに移動します</h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">×</button>
        </div>
        <div className="external-link-modal__app">
          <span className={`app-icon app-icon--md ${!app.iconUrl ? `app-icon--tone${toneForApp(app)}` : ''}`}>
            {app.iconUrl ? <img src={app.iconUrl} alt="" /> : app.name.charAt(0)}
          </span>
          <div className="external-link-modal__app-info">
            <div className="external-link-modal__app-name">{app.name}</div>
            <div className="external-link-modal__app-url">{app.url}</div>
          </div>
        </div>
        <ul className="modal__list">
          <li>移動先は VRMA Hub の外部サイトです。</li>
          <li>コンテンツや個人情報の取り扱いは、提供元の利用規約・プライバシーポリシーに従います。</li>
        </ul>
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>キャンセル</button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} ref={confirmBtnRef}>
            移動する
            <svg width="11" height="11" viewBox="0 0 12 11" fill="none" aria-hidden="true">
              <path d="M4 2h6v6M10 2 3.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
